<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, ListChecks, Search, ShieldCheck } from "lucide-vue-next";
import type { BriefSolution } from "@/types";

const props = defineProps<{ solution: BriefSolution | null; confirming?: boolean }>();
const emit = defineEmits<{ confirm: [] }>();
const tasks = computed(() => props.solution?.tasks || []);
const solutionPanel = ref<HTMLElement | null>(null);
const mobilePage = ref(0);
const activeTaskIndex = computed(() => Math.min(Math.max(mobilePage.value - 1, 0), Math.max(tasks.value.length - 1, 0)));
const confirmPageIndex = computed(() => tasks.value.length + 1);
const mobilePageCount = computed(() => tasks.value.length + 2);
const mobilePageLabel = computed(() => {
  if (mobilePage.value === 0) return "方案概览";
  if (mobilePage.value === confirmPageIndex.value) return "核对并确认";
  return `任务 ${mobilePage.value}：${tasks.value[mobilePage.value - 1]?.titleClient || "执行任务"}`;
});
let touchStartX = 0;

watch(
  () => [props.solution?.id, tasks.value.length],
  () => {
    mobilePage.value = 0;
  },
);

async function showMobilePage(index: number) {
  mobilePage.value = Math.min(Math.max(index, 0), mobilePageCount.value - 1);
  await nextTick();
  const scroller = solutionPanel.value?.closest(".task-panel") as HTMLElement | null;
  scroller?.scrollTo({ top: 0, behavior: "smooth" });
}

function handleTouchStart(event: TouchEvent) {
  touchStartX = event.changedTouches[0]?.clientX || 0;
}

function handleTouchEnd(event: TouchEvent) {
  if (!window.matchMedia("(max-width: 700px)").matches) return;
  const endX = event.changedTouches[0]?.clientX || touchStartX;
  const distance = endX - touchStartX;
  if (Math.abs(distance) < 48) return;
  void showMobilePage(mobilePage.value + (distance < 0 ? 1 : -1));
}
</script>

<template>
  <section ref="solutionPanel" class="solution-panel" @touchstart.passive="handleTouchStart" @touchend.passive="handleTouchEnd">
    <template v-if="solution">
      <nav class="mobile-solution-pages" aria-label="方案页面">
        <button type="button" :class="{ active: mobilePage === 0 }" :aria-current="mobilePage === 0 ? 'page' : undefined" @click="showMobilePage(0)">概览</button>
        <button
          v-for="(task, index) in tasks"
          :key="task.stableKey"
          type="button"
          :class="{ active: mobilePage === index + 1 }"
          :aria-label="`任务 ${index + 1}：${task.titleClient}`"
          :aria-current="mobilePage === index + 1 ? 'page' : undefined"
          @click="showMobilePage(index + 1)"
        >{{ index + 1 }}</button>
        <button type="button" :class="{ active: mobilePage === confirmPageIndex }" :aria-current="mobilePage === confirmPageIndex ? 'page' : undefined" @click="showMobilePage(confirmPageIndex)">确认</button>
      </nav>
      <section class="solution-overview-page" :class="{ 'mobile-active': mobilePage === 0 }">
        <header>
          <div>
            <span class="eyebrow">完整方案</span>
            <h2>{{ solution.clientContent.title || "等待 AI 整理方案" }}</h2>
          </div>
          <span class="readiness">完整度 {{ solution.readiness.score }}%</span>
        </header>
        <div class="solution-copy">{{ solution.clientContent.fullPlan || solution.clientContent.summary }}</div>
        <section v-if="solution.clientContent.researchSources?.length" class="research">
          <h3><Search :size="17" />联网参考</h3>
          <p v-if="solution.clientContent.researchSummary">{{ solution.clientContent.researchSummary }}</p>
          <a v-for="source in solution.clientContent.researchSources" :key="source.url" :href="source.url" target="_blank" rel="noopener noreferrer"><span><b>{{ source.title }}</b><small>{{ source.url }}</small></span><ExternalLink :size="14" /></a>
        </section>
      </section>
      <section class="solution-tasks" :class="{ 'mobile-active': mobilePage > 0 && mobilePage < confirmPageIndex }">
        <header class="task-pager-head">
          <h3><ListChecks :size="17" />两级执行清单</h3>
          <span v-if="tasks.length">{{ activeTaskIndex + 1 }} / {{ tasks.length }}</span>
        </header>
        <div class="task-pages">
          <article
            v-for="(task, index) in tasks"
            :key="task.stableKey"
            :class="{ active: index === activeTaskIndex }"
          >
            <span>{{ index + 1 }}</span>
            <div>
              <b>{{ task.titleClient }}</b>
              <p>{{ task.scopeClient }}</p>
              <div class="task-meta"><CalendarDays :size="14" />{{ task.plannedStartAt }} 至 {{ task.plannedEndAt }}</div>
              <ol class="groups">
                <li v-for="group in task.checklist" :key="group.stableKey">
                  <div class="group-head"><b>{{ group.title }}</b><time>{{ group.plannedStartAt }} 至 {{ group.plannedEndAt }}</time></div>
                  <p>{{ group.description }}</p>
                  <ul>
                    <li v-for="child in group.children" :key="child.stableKey">
                      <div><b>{{ child.title }}</b><time>{{ child.plannedStartAt }} 至 {{ child.plannedEndAt }}</time></div>
                      <p>{{ child.description }}</p>
                      <small>核查：{{ child.acceptanceCriteria.join("；") }}</small>
                    </li>
                  </ul>
                </li>
              </ol>
            </div>
          </article>
        </div>
      </section>
      <section class="solution-confirm-page" :class="{ 'mobile-active': mobilePage === confirmPageIndex }">
        <header>
          <span class="eyebrow">最后一步</span>
          <h2>核对并确认方案</h2>
        </header>
        <section v-if="solution.requirements?.length" class="requirements">
          <h3><ShieldCheck :size="17" />提交前核查事项</h3>
          <article v-for="item in solution.requirements" :key="item.id">
            <b>{{ item.title }}</b><p>{{ item.description }}</p>
          </article>
        </section>
        <div v-if="solution.readiness.warnings.length" class="warnings">
          <b>仍可补充</b>
          <ul><li v-for="item in solution.readiness.warnings" :key="item">{{ item }}</li></ul>
        </div>
        <button
          class="btn btn--primary confirm"
          :disabled="!solution.readiness.readyForClientReview || confirming"
          @click="emit('confirm')"
        >
          <CheckCircle2 :size="17" />
          {{ confirming ? "正在创建项目草稿" : "确认方案并创建项目草稿" }}
        </button>
        <p v-if="!solution.readiness.readyForClientReview" class="muted gate">
          方案仍缺少：{{ solution.readiness.missingBlockingFields.join("、") }}
        </p>
      </section>
      <nav class="mobile-solution-nav" aria-label="切换方案页面">
        <button type="button" title="上一页" :disabled="mobilePage === 0" @click="showMobilePage(mobilePage - 1)"><ChevronLeft :size="20" /></button>
        <span>{{ mobilePage + 1 }} / {{ mobilePageCount }} · {{ mobilePageLabel }}</span>
        <button type="button" title="下一页" :disabled="mobilePage >= mobilePageCount - 1" @click="showMobilePage(mobilePage + 1)"><ChevronRight :size="20" /></button>
      </nav>
    </template>
    <template v-else>
      <header><div><span class="eyebrow">完整方案</span><h2>等待 AI 整理方案</h2></div></header>
      <p class="muted empty">发送需求后，AI 的完整方案会显示在这里。</p>
    </template>
  </section>
</template>

<style scoped>
.solution-panel,
.solution-overview-page,
.solution-confirm-page {
  display: grid;
  gap: 16px;
}
.solution-overview-page > header,
.solution-confirm-page > header,
.solution-panel > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.solution-panel h2,
.solution-panel h3 {
  margin: 0;
}
.solution-panel h2 { font-size: 18px; }
.eyebrow { display: block; margin-bottom: 3px; color: var(--muted); font-size: 12px; }
.readiness { white-space: nowrap; padding: 4px 7px; background: #e8f5f1; color: var(--success); font-size: 12px; }
.solution-copy { white-space: pre-wrap; padding: 14px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); line-height: 1.7; }
.research { display: grid; gap: 6px; padding: 12px 0; border-bottom: 1px solid var(--line); }
.research h3,
.solution-tasks h3,
.requirements h3 { display: flex; align-items: center; gap: 7px; font-size: 15px; }
.research > p { margin: 0; color: var(--muted); }
.research a { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px; border: 1px solid var(--line); color: var(--ink); text-decoration: none; }
.research a span { min-width: 0; }
.research a b,
.research a small { display: block; }
.research a small { overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.solution-tasks { display: grid; gap: 8px; }
.task-pager-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.task-pager-head > span { display: none; color: var(--muted); font-size: 12px; }
.task-pages > article { display: grid; grid-template-columns: 26px minmax(0, 1fr); gap: 8px; padding: 12px 0; border-bottom: 1px solid var(--line); }
.solution-tasks article > span { display: grid; width: 24px; height: 24px; place-items: center; background: #f1f4f4; color: var(--muted); font-size: 12px; }
.solution-tasks p { margin: 3px 0; color: var(--muted); }
.task-meta { display: flex; align-items: center; gap: 5px; margin-top: 7px; color: var(--muted); font-size: 12px; }
.groups { display: grid; gap: 9px; margin: 12px 0 0; padding: 0; list-style: none; }
.groups > li { padding: 10px; border-left: 3px solid #81949a; background: #f8fafa; }
.group-head,
.groups ul li > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.groups time { white-space: nowrap; color: var(--muted); font-size: 11px; }
.groups ul { display: grid; gap: 7px; margin: 9px 0 0; padding: 0 0 0 18px; list-style: none; }
.groups ul li { padding: 8px 0; border-top: 1px solid var(--line); }
.groups small { display: block; margin-top: 4px; color: var(--brand-dark); }
.requirements { display: grid; gap: 0; padding: 13px 0; border-top: 1px solid var(--line); }
.requirements article { padding: 9px 0; border-bottom: 1px solid var(--line); }
.requirements p { margin: 3px 0; color: var(--muted); }
.warnings { padding: 11px; border-left: 3px solid #d98b21; background: #fff8ed; }
.warnings ul { margin: 5px 0 0; padding-left: 18px; }
.confirm { display: flex; align-items: center; justify-content: center; gap: 7px; width: 100%; }
.gate,
.empty { margin: 0; text-align: center; }
.mobile-solution-pages,
.mobile-solution-nav { display: none; }

@media (max-width: 700px) {
  .solution-panel { display: block; min-height: 100%; touch-action: pan-y; }
  .mobile-solution-pages {
    position: sticky;
    top: 0;
    z-index: 4;
    display: flex;
    gap: 6px;
    margin: -16px -14px 14px;
    padding: 9px 10px;
    overflow-x: auto;
    border-bottom: 1px solid var(--line);
    background: #fff;
  }
  .mobile-solution-pages button {
    flex: 0 0 auto;
    min-width: 38px;
    height: 36px;
    border: 1px solid var(--line);
    padding: 0 10px;
    background: #fff;
    color: var(--muted);
    font-weight: 700;
  }
  .mobile-solution-pages button.active { border-color: var(--brand); background: var(--brand); color: #fff; }
  .solution-overview-page,
  .solution-tasks,
  .solution-confirm-page { display: none; min-height: calc(100dvh - 250px); }
  .solution-overview-page.mobile-active,
  .solution-tasks.mobile-active,
  .solution-confirm-page.mobile-active { display: grid; }
  .task-pager-head > span { display: block; }
  .task-pages > article { display: none; padding: 14px 0; }
  .task-pages > article.active { display: grid; }
  .mobile-solution-nav {
    position: sticky;
    bottom: 0;
    z-index: 4;
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) 44px;
    align-items: center;
    gap: 8px;
    margin: 18px -14px -16px;
    padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--line);
    background: #fff;
  }
  .mobile-solution-nav button { display: grid; width: 44px; height: 44px; place-items: center; border: 1px solid var(--line); background: #fff; color: var(--ink); }
  .mobile-solution-nav button:disabled { opacity: .32; }
  .mobile-solution-nav span { min-width: 0; overflow: hidden; color: var(--muted); font-size: 12px; text-align: center; text-overflow: ellipsis; white-space: nowrap; }
  .group-head,
  .groups ul li > div { display: grid; }
  .groups time { white-space: normal; }
  .groups ul { padding-left: 10px; }
  .groups > li { padding: 10px 8px; }
  .solution-tasks article > span { width: 26px; height: 26px; }
}
</style>
